/**
 * PageHeader — title, optional subtitle, and a right-aligned actions slot on one line.
 *
 * Replaces the "title on its own row, primary button hand-rolled in a row
 * underneath" pattern. `Breadcrumbs` still renders the title-only row for every
 * other module and is deliberately left untouched; this is the component pages
 * adopt when they want their primary action beside the title.
 *
 * Two variants:
 *   - `page`    (default) top-of-page header. Keeps the app-wide
 *               `.page-header` / `.page-title` classes so vertical rhythm and
 *               typography are identical to every other screen.
 *   - `section` header for a block inside a page — e.g. a list rendered inside
 *               a tab panel, where `.page-header`'s 30px margin and 26px title
 *               would be wrong. Matches the `h4 + d-flex` markup the masters
 *               lists already hand-rolled.
 *
 * `children` renders as a toolbar row directly beneath the title row, inside the
 * same header block. Filters live there rather than on the title line: the title
 * row must stay short enough that it never wraps, otherwise the title/action
 * pairing this component exists to guarantee is the first thing to break at
 * tablet widths.
 *
 * Buttons passed into `actions` should all use the same size class (`btn-sm`
 * across Uniform Management) — this component sets the layout, not the sizing.
 */
const PageHeader = ({
  title,
  subtitle,
  actions,
  variant = 'page',
  className = '',
  children,
}) => {
  const isSection = variant === 'section';
  const wrapperClass = isSection
    ? `mb-3 ${className}`.trim()
    : `page-header ${className}`.trim();
  const Heading = isSection ? 'h4' : 'h3';

  return (
    <div className={wrapperClass}>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div className="me-auto">
          <Heading className={isSection ? 'mb-0' : 'page-title mb-0'}>{title}</Heading>
          {subtitle && (
            <div className="text-muted mt-1" style={{ fontSize: 13 }}>
              {subtitle}
            </div>
          )}
        </div>
        {actions && (
          <div className="d-flex align-items-center flex-wrap gap-2">{actions}</div>
        )}
      </div>
      {children && <div className="mt-3">{children}</div>}
    </div>
  );
};

export default PageHeader;
